<template>
  <ExpandableTransition>
    <template #action="{ open, close, isExpanded }">
      <div class="flex w-full justify-center">
        <button
          class="flex w-full justify-center py-3"
          type="button"
          @click="isExpanded.value ? close() : open()"
        >
          <span
            class="flex transition-transform duration-300"
            :class="isExpanded.value ? 'rotate-180' : 'rotate-0'"
          >
            <!-- prettier-ignore -->
            <svg width="36" height="8" viewBox="0 0 36 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clip-path="url(#clip0_13905_42990)">
            <path opacity="0.4" fill-rule="evenodd" clip-rule="evenodd" d="M0.127395 6.96185C0.515236 7.78924 1.66806 8.20845 2.7023 7.89818L18.0001 3.30886L33.2978 7.89818C34.332 8.20845 35.4849 7.78924 35.8727 6.96185C36.2606 6.13446 35.7365 5.2122 34.7023 4.90193L18.7023 0.10193C18.2495 -0.0339022 17.7506 -0.0339022 17.2978 0.10193L1.29781 4.90193C0.263566 5.2122 -0.260445 6.13446 0.127395 6.96185Z" fill="#AEAEB2"/>
            </g>
            <defs>
            <clipPath id="clip0_13905_42990">
            <rect width="36" height="8" fill="white" transform="matrix(1 0 0 -1 0 8)"/>
            </clipPath>
            </defs>
          </svg>
          </span>
        </button>
      </div>
    </template>
    <div
      v-if="priceBreakdown"
      class="text-text-neutral-primary -mt-3 mb-3 space-y-2 text-sm"
    >
      <div class="flex items-center justify-between">
        <p>{{ t('booking.airTicket') }}</p>
        <p v-price="priceBreakdown.basePrice" />
      </div>
      <div
        v-if="priceBreakdown.packageTitle"
        class="flex items-center justify-between"
      >
        <p>
          {{ t('booking.package', { title: priceBreakdown.packageTitle }) }}
        </p>
        <p v-price="priceBreakdown.packagePrice" />
      </div>
      <div
        v-if="priceBreakdown.baggagePrice > 0"
        class="flex items-center justify-between"
      >
        <p>{{ t('ancillaries.baggages.title') }}</p>
        <div class="flex items-center space-x-2">
          <p v-price="priceBreakdown.baggagePrice" />
          <p
            v-if="
              hasOriginalPrice(
                priceBreakdown.baggageOriginalPrice,
                priceBreakdown.baggagePrice,
              )
            "
            v-price="priceBreakdown.baggageOriginalPrice"
            class="text-text-neutral-quaternary text-xs font-medium line-through"
          />
        </div>
      </div>
      <div
        v-if="priceBreakdown.seatMapPrice !== null"
        class="flex items-center justify-between"
      >
        <p>{{ t('ancillaries.seatMap.title') }}</p>
        <div class="flex items-center space-x-2">
          <p v-price="priceBreakdown.seatMapPrice" />
          <p
            v-if="
              hasOriginalPrice(
                priceBreakdown.seatMapOriginalPrice,
                priceBreakdown.seatMapPrice,
              )
            "
            v-price="priceBreakdown.seatMapOriginalPrice"
            class="text-text-neutral-quaternary text-xs font-medium line-through"
          />
        </div>
      </div>
      <div
        v-if="!isNil(priceBreakdown.insurancePrice)"
        class="flex items-center justify-between"
      >
        <p>{{ t('ancillaries.medicalInsurance.title') }}</p>
        <p v-price="priceBreakdown.insurancePrice" />
      </div>
      <div
        v-if="!isNil(priceBreakdown.roamingPrice)"
        class="flex items-center justify-between"
      >
        <p>{{ t('roaming.roaming') }}</p>
        <p v-price="priceBreakdown.roamingPrice" />
      </div>
      <template v-if="priceBreakdown.selectedProducts?.length > 0">
        <div
          v-for="product in priceBreakdown.selectedProducts"
          :key="product.id"
          class="flex items-center justify-between"
        >
          <p>{{ product?.title }}</p>
          <p v-price="product.price.amount" />
        </div>
      </template>
      <div
        v-if="priceBreakdown.flightDelayPrice > 0"
        class="flex items-center justify-between"
      >
        <p>{{ t('ancillaries.flightDelay.title') }}</p>
        <p v-price="priceBreakdown.flightDelayPrice" />
      </div>
    </div>
  </ExpandableTransition>
</template>

<script lang="ts" setup>
  import { useI18n } from 'vue-i18n';
  import { ExpandableTransition } from 'shared';
  import { isNil } from 'lodash-es';
  import type { MarkedProduct } from '@aviata/products-fns';

  const { t } = useI18n();

  function hasOriginalPrice(
    originalPrice: number | null,
    currentPrice: number | null,
  ) {
    return (
      !isNil(originalPrice) &&
      !isNil(currentPrice) &&
      originalPrice > currentPrice
    );
  }

  defineProps<{
    priceBreakdown: {
      basePrice: number;
      packagePrice: number;
      packageTitle: string;
      productsPrice: number;
      insurancePrice: number | null;
      baggagePrice: number | null;
      baggageOriginalPrice: number | null;
      seatMapPrice: number | null;
      seatMapOriginalPrice: number | null;
      roamingPrice: number | null;
      isRoamingSelected: boolean;
      selectedProducts: MarkedProduct[];
    };
  }>();
</script>
